/**
 * ERPProcurementGrid — Procurement-module-specific grid wrapper.
 *
 * Extends ERPAdvancedGrid with:
 *  - Locale-aware amount formatting (Indian en-IN / International en-US)
 *  - Fixed-width amount columns (110px — fits 9,99,99,999.99 + tabular alignment)
 *  - "Print Document" procurement-specific context menu action (onPrintDoc)
 *  - Passes onVoid through ERPAdvancedGrid (void confirmation built into ERPActionColumn)
 *
 * Architecture: Same pattern as ERPFinanceGrid — each domain owns its grid wrapper.
 *   ERPFinanceGrid    → Finance vouchers (Dr/Cr, account history, ledger)
 *   ERPProcurementGrid → GRN, Party Bill, Purchase Invoice (doc print, supplier tracking)
 *   ERPInventoryGrid  → (future) stock movements, item lookup
 *   ERPSalesGrid      → (future) sales invoices, customer history
 */
import React, { useCallback } from 'react';
import ERPAdvancedGrid from '../erp/ERPAdvancedGrid';

const PROC_AMOUNT_FIELDS = [
  'grossAmount', 'netAmount', 'returnAmount',
  'TotalValue', 'totalAmount', 'NetAmount', 'GrossAmount',
  'TaxAmount', 'Discount', 'discount', 'Freight', 'freight',
  'CreditAmount', 'DebitAmount',
  'DrAmount', 'Cramount', 'CrAmount', 'totalDr', 'totalCr', 'Amount',
  'Rate', 'rate', 'Qty', 'qty',
];

const AMOUNT_WIDTH = 110; // px

const ERPProcurementGrid = ({
  amountLocale = 'en-IN',
  onPrintDoc,           // (row) => void  — print GRN/invoice doc
  extraAmountFields = [],
  ...props
}) => {
  const amountFields = [...PROC_AMOUNT_FIELDS, ...extraAmountFields];

  const fmtAmount = useCallback((v) => {
    const num = parseFloat(v);
    if (isNaN(num)) return '—';
    return num.toLocaleString(amountLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, [amountLocale]);

  const cellRenderer = useCallback((field, value, row) => {
    if (amountFields.includes(field)) {
      const num = parseFloat(value);
      return (
        <span style={{
          fontFamily: 'var(--erp-font-mono)',
          fontSize: 11,
          fontVariantNumeric: 'tabular-nums',
          display: 'block',
          textAlign: 'right',
        }}>
          {isNaN(num) ? '—' : fmtAmount(num)}
        </span>
      );
    }
    return props.cellRenderer ? props.cellRenderer(field, value, row) : null;
  }, [amountFields, fmtAmount, props.cellRenderer]);

  const cellClassName = useCallback((field, row) => {
    if (amountFields.includes(field)) return 'text-right';
    return props.cellClassName ? props.cellClassName(field, row) : '';
  }, [amountFields, props.cellClassName]);

  // Inject fixed width onto amount columns
  const columns = (props.columns || []).map(c =>
    amountFields.includes(c.field)
      ? { ...c, width: c.width || AMOUNT_WIDTH }
      : c
  );

  return (
    <ERPAdvancedGrid
      {...props}
      columns={columns}
      onPrint={onPrintDoc || props.onPrint}
      cellRenderer={cellRenderer}
      cellClassName={cellClassName}
    />
  );
};

export default ERPProcurementGrid;
